import { isNative } from '@/lib/platform'
import { createWebPowerSync } from './client-web'
import { createNativePowerSync } from './client-native'
import { logPsLifecycle } from './lifecycle-log'
import type { PowerSyncDatabase as WebPS } from '@powersync/web'

// Both Web and Native SDKs implement AbstractPowerSyncDatabase (the native SDK
// extends WebPowerSyncDatabase directly). We surface the web type here because
// every existing caller uses it; the native SDK is structurally compatible at
// the methods we use (execute, watch, registerListener, disconnect, close).
type PS = WebPS

let instance: PS | null = null
// Monotonic construct counter. Numbering each create in the lifecycle log makes
// a recreate loop (the code-5 cause) obvious at a glance: a healthy session has
// exactly one "create #1".
let createCount = 0
// Non-null while a close() is in flight. The capacitor SQLite adapter keys its
// native read+write connections by dbFilename GLOBALLY (CapacitorConnectionPool
// .init() force-closes any existing 'imki.sqlite' connection before opening its
// own). So a second PowerSyncDatabase constructed while the first is still
// disconnecting/closing rips the write connection out mid-sync → "ExecuteSet:
// database is locked (code 5)". This gate guarantees creation never overlaps a
// teardown: exactly one live instance at any time.
let teardown: Promise<void> | null = null

// Armed just before a deliberate full-document navigation OUT of the SPA (the
// static /anleitung manual lives outside the route tree, so it needs a hard
// load). Once armed, getPowerSync() refuses to construct: an instance created
// in the milliseconds between closePowerSync() and the document unload would be
// abandoned mid-open by the navigation and leave imki.sqlite locked for the
// NEXT document — the exact code-5 collision a full reload causes on native.
// The flag dies with the document, so it self-clears on the next load.
let navigatingAway = false
export function beginExternalNavigation(): void {
  navigatingAway = true
  logPsLifecycle('create-blocked', 'external navigation')
}

export function getPowerSync(): PS | null {
  if (typeof window === 'undefined') return null
  // A hard navigation out of the SPA is imminent (see `navigatingAway`).
  // Constructing now would spawn an instance that the unload abandons mid-open.
  if (navigatingAway) return null
  // Skip opening imki.sqlite when the page was just loaded from a nuke. The
  // PostNukeCleanup effect will finish deleting any IDBs that were blocked
  // during nuke() itself; if we open PowerSync here in parallel, the delete
  // is blocked for ~7–10s of retries. User must reload (or navigate without
  // ?nuked=) to reopen PowerSync, which is fine — post-nuke they're always
  // on /login anyway.
  if (window.location.search.includes('nuked=')) return null
  // A teardown is in flight — constructing now would collide on the native
  // connection (see `teardown` above). Return null; callers treat it as "not
  // ready yet" (PowerSyncProvider re-runs on the next auth/route change;
  // intent-worker & background tasks already null-guard getPowerSync()).
  if (teardown) {
    logPsLifecycle('create-blocked', 'teardown in flight')
    return null
  }
  if (!instance) {
    logPsLifecycle('create', `#${++createCount} (${isNative() ? 'native' : 'web'})`)
    instance = (isNative() ? createNativePowerSync() : createWebPowerSync()) as PS | null
  }
  return instance
}

// Tear down for a logged-out user. MUST close(), not just disconnect():
// disconnect() stops sync but leaves the native @capacitor-community/sqlite
// read+write connections to imki.sqlite OPEN. Closing releases them so a later
// recreate (logout→login) doesn't collide. Delegates to closePowerSync.
export async function disconnectPowerSync() {
  await closePowerSync()
}

// Full teardown: disconnect + close the SQLite connection (also used by the
// debug nuke flow so the backing `imki.sqlite` IDB can be deleted without
// `onblocked`). Captures the instance locally and coalesces concurrent callers
// onto one teardown promise, so a racing close can't disconnect()/close() an
// instance that another close already replaced.
export async function closePowerSync(): Promise<void> {
  if (teardown) {
    logPsLifecycle('close-coalesced')
    return teardown
  }
  const closing = instance
  if (!closing) return
  instance = null
  logPsLifecycle('close-start')
  teardown = (async () => {
    try { await closing.disconnect() } catch { /* noop */ }
    try { await closing.close() } catch { /* noop */ }
  })().finally(() => {
    teardown = null
    logPsLifecycle('close-done')
  })
  return teardown
}
